import { isDefined } from '@sgme/fp';
import { createSelector } from 'reselect';
import { AppState } from 'state/state.model';
import { getWorkspaceTabIds } from 'state/workspaces/workspaces/workspaces.selector';

import type { TabType, WorkspaceType } from '../model';
import type { TabLoadingStatus, WorkspaceTab } from './tabs.model';

export interface TabTitle {
  customizedName: string | undefined;
  tabIndex: number;
  workspaceType: WorkspaceType;
}

export function getTabTitle(state: AppState, tabId: string): TabTitle | undefined {
  const tab = state.workspaces.tabs[tabId];
  if (!tab) {
    return undefined;
  }
  return {
    customizedName: tab.name,
    tabIndex: tab.count,
    workspaceType: tab.workspace,
  };
}

export function getTabLoadingStatus(state: AppState, tabId: string): TabLoadingStatus | undefined {
  const tab = state.workspaces.tabs[tabId];
  if (!tab) {
    return undefined;
  }
  return tab.loadingStatus;
}
export function getTabIsBusy(state: AppState, tabId: string): boolean | undefined {
  const tab = state.workspaces.tabs[tabId];
  if (!tab) {
    return undefined;
  }
  return tab.isBusy;
}

export function getTabIsFrozen(state: AppState, tabId: string): boolean | undefined {
  const tab = state.workspaces.tabs[tabId];
  if (!tab) {
    return undefined;
  }
  return tab.isFrozen;
}
export function getTabType(state: AppState, tabId: string | undefined): TabType | undefined {
  if (!tabId) {
    return undefined;
  }
  return getWorkspaceTab(state, tabId)?.type;
}

export function getWorkspaceType(state: AppState, tabId: string): WorkspaceType | undefined {
  const tab = state.workspaces.tabs[tabId];
  return tab ? tab.workspace : undefined;
}
export function hasWorkspaceTabs(state: AppState, workspace: WorkspaceType): boolean {
  return getWorkspaceTabIds(state, workspace)?.length > 0;
}
export function getWorkspaceTab(state: AppState, tabId: string): WorkspaceTab | undefined {
  return state.workspaces.tabs[tabId];
}
export function getWorkspaceTabError(state: AppState, tabId: string): string | undefined {
  return state.workspaces.tabs[tabId]?.error;
}
export function hasWorkspaceTabError(state: AppState, tabId: string): boolean {
  return isDefined(getWorkspaceTabError(state, tabId));
}
export function getWorkspaceTabTraceId(state: AppState, tabId: string): string | undefined {
  return state.workspaces.tabs[tabId]?.traceId;
}

// This function return a new reference after each call, therefore it should not by used directly by a component.
export const getWorkspaceTabs: (state: AppState, workspace: WorkspaceType) => WorkspaceTab[] = createSelector(
  [
    (state: AppState, _workspace: WorkspaceType) => state.workspaces.tabs,
    (_state: AppState, workspace: WorkspaceType) => workspace,
  ],
  (tabs, workspace) => {
    return Object.values(tabs).filter(tab => tab.workspace === workspace);
  },
);

export function usesDynamicRFQEnrichment(state: AppState, tabId: string): boolean {
  return (
    getWorkspaceType(state, tabId) === 'dynamic' ||
    (getWorkspaceType(state, tabId) === 'postTrade' && getTabType(state, tabId) === 'productModification')
  );
}
